import { useState, useEffect } from 'react';
import { Check, Copy, LoaderCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { glacierPolicyApiGetLatestTransactionLatestAttemptOptions } from '@/lib/heyapi/@tanstack/react-query.gen';

import { useQuery } from '@tanstack/react-query';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const DebugTransaction = ({
  accountId,
  setLoadingDebug,
}: {
  accountId: string;
  setLoadingDebug: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);
  const { data: latestAttempt, isLoading } = useQuery({
    ...glacierPolicyApiGetLatestTransactionLatestAttemptOptions({
      path: {
        account_id: accountId,
      },
    }),
  });

  useEffect(() => {
    if (!isLoading) {
      setLoadingDebug(false);
    }
  }, [setLoadingDebug, isLoading]);

  if (isLoading) {
    return (
      <p className="flex items-center">
        <LoaderCircle className="mr-2 h-10 w-10 animate-spin text-brand-200" />
        Fetching Latest transaction attempt...
      </p>
    );
  }

  const copyToClipboard = (
    text: string,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  if (latestAttempt) {
    const { request, response } = latestAttempt;

    return (
      <div className="max-w-screen grid h-[80vh] w-full grid-cols-2 gap-4">
        <Card className="overflow-scroll">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Request to Rater</CardTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                copyToClipboard(
                  JSON.stringify(request, null, 2),
                  setCopiedRequest
                )
              }
              aria-label="Copy request JSON"
            >
              {copiedRequest ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </CardHeader>
          <CardContent>
            <code>{JSON.stringify(latestAttempt?.request, null, 2)}</code>
          </CardContent>
        </Card>

        <Card className="overflow-scroll">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Response from Rater</CardTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                copyToClipboard(
                  JSON.stringify(response, null, 2),
                  setCopiedResponse
                )
              }
              aria-label="Copy request JSON"
            >
              {copiedResponse ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </CardHeader>
          <CardContent>
            <code>{JSON.stringify(latestAttempt?.response, null, 2)}</code>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <p>
      {`No rating has been recorded yet. When you see "Policy Rated" or "Error
      While Rating Policy", come back here`}
    </p>
  );
};
